import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getDefaultStore } from 'jotai';

import { useFeatureFlag } from '#components/feature-flag/use-feature-flag.js';
import { useTRPC } from '#src/trpc.js';
import { lastOpenProjectAtom } from '#state/atoms/last-open-project-atom.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { ProjectTriageList } from './project-triage-list.js';
import { ProjectsEmptyState } from './projects-empty-state.js';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/projects/')({
	staticData: {
		authorize: (ability) => ability.can('view', 'project'),
		navigation: {
			label: 'Projects',
			keywords: ['projects', 'home', 'list'],
		},
	},
	beforeLoad: () => {
		getDefaultStore().set(lastOpenProjectAtom, (prev) => ({
			...prev,
			projectId: null,
			revisionId: null,
		}));
	},
	head: () => ({ meta: [{ title: 'Projects • Cadstrom' }] }),
	component: ProjectsHomePage,
});

export function ProjectsHomePage() {
	const trpc = useTRPC();
	const { orgName } = Route.useParams();

	const { data, isPending } = useQuery(trpc.project.list.queryOptions({ organizationName: orgName }));

	const isDemoProjectFlagEnabled = useFeatureFlag('demo-project');

	const previews = data?.projectPreviews ?? [];
	const demoProject = previews.find((preview) => preview.isDemo && preview.latestRevision !== null) ?? null;
	// New orgs are seeded with a demo, so the two-door onboarding state covers any org without a
	// project of its own; the demo's door opens it directly. Without the flag there is no sample
	// door, so a demo-only org shows the normal list instead.
	const hasOwnProject = previews.some((preview) => !preview.isDemo);
	const isEmpty =
		data !== undefined &&
		(previews.length === 0 || (isDemoProjectFlagEnabled === true && !hasOwnProject && demoProject !== null));

	const el = useDataElTagGenerator(ProjectsHomePage);
	return (
		<div {...el()} className='flex min-h-0 grow basis-0 flex-col items-center'>
			<div {...el('content')} className='flex min-h-0 w-full max-w-280 grow flex-col px-4 py-4'>
				{isEmpty ?
					<ProjectsEmptyState orgName={orgName} demoProject={demoProject} />
				:	<ProjectTriageList projects={previews} isLoading={isPending} orgName={orgName} />}
			</div>
		</div>
	);
}
